/**
 * ShopAcceptInvite — Customer sets portal password from wholesaler invite link
 * URL: /shop/:businessId/accept-invite?token=...
 */
import React, { useState } from 'react';
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom';
import API_URL from '../../config/api';

const ShopAcceptInvite: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!token) {
      setError('Missing invitation token. Use the link from your email.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/shop-auth/accept-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password, businessId }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Could not activate account');
        return;
      }
      localStorage.setItem(`shopToken_${businessId}`, data.token);
      localStorage.setItem(`shopCustomer_${businessId}`, JSON.stringify(data.customer));
      navigate(`/shop/${businessId}/dashboard`);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f0f4ff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui, sans-serif', padding: '24px 16px' }}>
      <div style={{ width: '100%', maxWidth: 440, marginBottom: 12 }}>
        <Link to={`/shop/${businessId}`} style={{ fontSize: 13, color: '#6b7280', textDecoration: 'none' }}>
          ← Back to shop
        </Link>
      </div>

      <div style={{ background: '#fff', borderRadius: 16, padding: '40px 36px', maxWidth: 440, width: '100%', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 44, marginBottom: 8 }}>🛒</div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#111827' }}>Activate your account</h1>
          <p style={{ margin: '6px 0 0', fontSize: 14, color: '#6b7280' }}>Choose a password to access your trade portal</p>
        </div>

        <form onSubmit={handleSubmit}>
          <label style={{ display: 'block', marginBottom: 16 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Password</span>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required style={{ width: '100%', marginTop: 6, padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db' }} />
          </label>
          <label style={{ display: 'block', marginBottom: 20 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Confirm password</span>
            <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required style={{ width: '100%', marginTop: 6, padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db' }} />
          </label>
          {error && <p style={{ color: '#b91c1c', fontSize: 13, marginBottom: 12 }}>{error}</p>}
          <button type="submit" disabled={loading} style={{ width: '100%', padding: 12, borderRadius: 10, border: 'none', background: '#1d4ed8', color: '#fff', fontWeight: 700, cursor: loading ? 'wait' : 'pointer' }}>
            {loading ? 'Activating…' : 'Activate account'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ShopAcceptInvite;
